/**
 * Microsoft Clarity custom events + tags.
 *
 * The base tag in index.html only collects default heatmaps/recordings.
 * These helpers fire business events ("start_quote", "checkout_start",
 * "checkout_complete", "concierge_submit", "ai_referral") so Clarity
 * recordings can be filtered to the funnel steps that matter.
 *
 * No PII: never pass emails, phones, names, or amounts as tag values.
 */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

const hasClarity = (): boolean =>
  typeof window !== "undefined" && typeof window.clarity === "function";

/** Fire a custom event (shows up as a filterable "event" in Clarity). */
export const clarityEvent = (name: string): void => {
  if (!hasClarity()) return;
  try {
    window.clarity!("event", name.slice(0, 64));
  } catch {
    /* analytics must never break the app */
  }
};

/** Set a custom tag (key/value filter in the Clarity dashboard). */
export const clarityTag = (key: string, value: string): void => {
  if (!hasClarity()) return;
  try {
    window.clarity!("set", key.slice(0, 64), value.slice(0, 120));
  } catch {
    /* analytics must never break the app */
  }
};

/** Fire an event only once per session (keyed by event name). */
export const clarityEventOnce = (name: string): void => {
  if (typeof window === "undefined") return;
  const key = `clarity_fired_${name}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* storage disabled — fall through and fire anyway */
  }
  clarityEvent(name);
};

/**
 * Permanently stop recording for internal people (admins, property managers,
 * owners). Keeps staff sessions out of the guest behaviour data.
 */
export const stopClarityForInternalUser = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem("av_no_analytics", "1");
  } catch {
    /* storage disabled */
  }
  if (!hasClarity()) return;
  try {
    window.clarity!("stop");
  } catch {
    /* analytics must never break the app */
  }
};

